/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { AllowedConnectorTypes } from '../config/allowed-connector-types';
import { AllowedFormatTypes } from '../config/allowed-format-types';
import { AllowedDataframeTypes } from '../config/allowed-statement-types';
import { useRecipe } from '../recipe-context';
import { extractNamesFromRecipe } from '../utils/extractNamesFromRecipe';
import { isValidNodeName } from '../utils/isValidNodeName';
import { useCustomReactFlowState } from './reactflow-context';

const NodeNamesContext = createContext(null);

export const useNodeNames = () => {
  const context = useContext(NodeNamesContext);
  if (!context) {
    throw new Error('useNodeNames must be used within a NodeNamesProvider');
  }
  return context;
};

const NodeNamesProvider = ({ children }) => {
  const { recipe } = useRecipe();
  const initialNodeData = extractNamesFromRecipe(recipe);
  const { setNodeError } = useCustomReactFlowState();

  // Map of nodeId -> {name, type}, initialized with initialNodeData if provided
  const [nodeNames, setNodeNames] = useState(() => {
    return initialNodeData || new Map();
  });

  // Map of name -> Set of node types using that name
  const [nameToNodeTypes, setNameToNodeTypes] = useState(() => {
    const map = new Map();
    if (initialNodeData) {
      for (const { name, type } of initialNodeData.values()) {
        if (name) {
          if (!map.has(name)) {
            map.set(name, new Set());
          }
          if (type) {
            map.get(name).add(type);
          }
        }
      }
    }
    return map;
  });

  // Set of all names for faster lookups
  const [nameSet, setNameSet] = useState(() => {
    const initialValues = initialNodeData
      ? Array.from(initialNodeData.values())
          .map((data) => data.name)
          .filter(Boolean)
      : [];
    return new Set(initialValues);
  });

  // Add effect to sync nameSet and nameToNodeTypes with nodeNames
  useEffect(() => {
    const newNameSet = new Set();
    const newNameToNodeTypes = new Map();

    for (const { name, type } of nodeNames.values()) {
      if (name) {
        newNameSet.add(name);
        if (!newNameToNodeTypes.has(name)) {
          newNameToNodeTypes.set(name, new Set());
        }
        if (type) {
          newNameToNodeTypes.get(name).add(type);
        }
      }
    }

    setNameSet(newNameSet);
    setNameToNodeTypes(newNameToNodeTypes);
  }, [nodeNames]);

  // Add effect to track recipe changes
  useEffect(() => {
    if (recipe && Object.keys(recipe).length > 0) {
      const newNodeData = extractNamesFromRecipe(recipe);
      setNodeNames(newNodeData);
    }
  }, [recipe]);

  /**
   * Sets a node's name. If the node already exists, this effectively renames it.
   * Enforces that the new name must be unique among all nodes, except for format nodes.
   * New rules:
   * - Dataframe nodes can have duplicate names among themselves
   * - Connector nodes cannot use names that are already used by dataframes
   * - Dataframe nodes cannot use names that are already used by connectors
   *
   * @param {string | number} nodeId - The unique identifier for the node.
   * @param {string} newName - The new name for the node.
   * @param {string} nodeType - The type of the node.
   * @throws Will throw an error if the new name is already taken by another node according to the rules.
   */
  const setOrRenameNodeName = useCallback(
    (nodeId, newName, nodeType) => {
      if (newName === '') {
        return;
      }
      if (!isValidNodeName(newName)) {
        setNodeError(nodeId, true);
        throw new Error('Node name is a reserved SQL keyword, use a different name');
      }

      // Check if the node is a format node using the AllowedFormatTypes array
      const isFormatNode = nodeType && AllowedFormatTypes.includes(nodeType);

      // Check if the node is a dataframe node using the AllowedDataframeTypes array
      const isDataframeNode = nodeType && AllowedDataframeTypes.includes(nodeType);

      // Check if the node is a connector node using the AllowedConnectorTypes array
      const isConnectorNode = nodeType && AllowedConnectorTypes.includes(nodeType);

      // Skip validation for format nodes (they can have any name)
      if (!isFormatNode) {
        const currentNodeData = nodeNames.get(nodeId);
        const isRenamingCurrentNode = currentNodeData && currentNodeData.name === newName;

        if (nameSet.has(newName)) {
          const typesUsingName = nameToNodeTypes.get(newName) || new Set();

          if (isDataframeNode) {
            // Dataframe nodes can only use names that are:
            // 1. Not used by any node, OR
            // 2. Only used by other dataframe nodes
            const hasNonDataframeTypes = Array.from(typesUsingName).some(
              (type) => !AllowedDataframeTypes.includes(type)
            );

            if (hasNonDataframeTypes) {
              setNodeError(nodeId, true);
              throw new Error('Node name is already taken, use a different name');
            } else {
              setNodeError(nodeId, false);
            }
          } else if (isConnectorNode) {
            // Connector nodes cannot use names that are used by any other node type (including dataframes)
            // Exception: If this is the only node using this name, it's okay
            const otherTypesUsingName = Array.from(typesUsingName).filter(
              (type) => type !== nodeType
            );

            if (otherTypesUsingName.length > 0) {
              setNodeError(nodeId, true);
              throw new Error('Node name is already taken, use a different name');
            } else {
              setNodeError(nodeId, false);
            }
          } else {
            // Other non-format nodes follow the original rule - no duplicates unless renaming current
            if (!isRenamingCurrentNode) {
              setNodeError(nodeId, true);
              throw new Error('Node name is already taken, use a different name');
            } else {
              setNodeError(nodeId, false);
            }
          }
        } else {
          setNodeError(nodeId, false);
        }
      } else {
        setNodeError(nodeId, false);
      }

      setNodeNames((prev) => {
        const _oldNodeData = prev.get(nodeId);
        const newMap = new Map(prev);
        newMap.set(nodeId, { name: newName, type: nodeType });
        return newMap;
      });
    },
    [nameSet, nodeNames, nameToNodeTypes, setNodeError]
  );

  /**
   * Removes a node's name from the state.
   *
   * @param {string | number} nodeId - The identifier of the node to remove.
   */
  const removeNodeName = useCallback((nodeId) => {
    setNodeNames((prev) => {
      const newMap = new Map(prev);
      newMap.delete(nodeId);
      return newMap;
    });
  }, []);

  /**
   * Returns an array of all current node names.
   *
   * @returns {string[]} An array of node names.
   */
  const getAllNodeNames = useCallback(() => {
    const names = Array.from(nameSet);
    return names;
  }, [nameSet]);

  /**
   * Checks whether a given name is already taken.
   *
   * @param {string} name - The name to check.
   * @returns {boolean} True if the name exists, false otherwise.
   */
  const isNameTaken = useCallback(
    (name) => {
      return nameSet.has(name);
    },
    [nameSet]
  );

  const value = {
    nodeNames,
    setOrRenameNodeName,
    removeNodeName,
    getAllNodeNames,
    isNameTaken,
  };

  return <NodeNamesContext.Provider value={value}>{children}</NodeNamesContext.Provider>;
};

export default NodeNamesProvider;
